import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber';
import { OrbitControls as OrbitControlsImpl } from 'three/examples/jsm/controls/OrbitControls';
import axios from 'axios';
import * as THREE from 'three';
import './Blog3DWorld.css';

extend({ OrbitControlsImpl });

const OrbitControls = () => {
  const { camera, gl } = useThree();
  const controls = useRef();
  
  useFrame(() => {
    if (controls.current) {
      controls.current.update();
    }
  });
  
  return (
    <orbitControlsImpl
      ref={controls}
      args={[camera, gl.domElement]}
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      maxPolarAngle={Math.PI / 2.2}
      minDistance={5}
      maxDistance={50}
    />
  );
};

// Enhanced Terrain with height variation
const EnhancedTerrain = ({ timeOfDay }) => {
  const terrainGeometry = useMemo(() => {
    const geometry = new THREE.PlaneGeometry(200, 200, 100, 100);
    
    // Add some height variation for a natural meadow look
    const positions = geometry.attributes.position.array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      
      // Create gentle hills
      positions[i + 2] = Math.sin(x * 0.05) * Math.cos(y * 0.05) * 2 +
                        Math.sin(x * 0.1) * 0.5 +
                        Math.cos(y * 0.1) * 0.5;
    }
    
    geometry.computeVertexNormals();
    return geometry;
  }, []);

  // Grass color changes based on time of day
  const grassColor = useMemo(() => {
    switch (timeOfDay) {
      case 'morning':
        return '#7EC850';
      case 'day':
        return '#6FB536';
      case 'evening':
        return '#5A8F2E';
      case 'night':
        return '#2D4A1B';
      default:
        return '#6FB536';
    }
  }, [timeOfDay]);

  return (
    <mesh 
      geometry={terrainGeometry} 
      rotation={[-Math.PI / 2, 0, 0]} 
      position={[0, -0.5, 0]}
      receiveShadow
    >
      <meshStandardMaterial
        color={grassColor}
        roughness={0.9}
        metalness={0.0}
      />
    </mesh>
  );
};

// Flowing River
const River = () => {
  const waterRef = useRef();

  useFrame((state) => {
    if (waterRef.current) {
      waterRef.current.material.uniforms.time.value = state.clock.elapsedTime;
    }
  });

  return (
    <mesh 
      ref={waterRef}
      position={[0, -0.3, -8]} 
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow
    >
      <planeGeometry args={[200, 8, 50, 20]} />
      <shaderMaterial
        uniforms={{
          time: { value: 0 },
          color: { value: new THREE.Color('#4A9FD8') }
        }}
        vertexShader={`
          uniform float time;
          varying vec2 vUv;
          varying float vElevation;
          
          void main() {
            vUv = uv;
            vec3 pos = position;
            
            float elevation = sin(pos.x * 0.5 + time * 0.5) * 0.1 +
                            cos(pos.y * 0.3 + time * 0.3) * 0.05;
            
            pos.z = elevation;
            vElevation = elevation;
            
            gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
          }
        `}
        fragmentShader={`
          uniform vec3 color;
          varying vec2 vUv;
          varying float vElevation;
          
          void main() {
            vec3 finalColor = color;
            finalColor = mix(finalColor, vec3(0.2, 0.5, 0.8), vElevation * 2.0);
            gl_FragColor = vec4(finalColor, 0.7);
          }
        `}
        transparent={true}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

// Trees
const Tree = ({ position, scale = 1 }) => {
  return (
    <group position={position} scale={scale}>
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.3, 3, 8]} />
        <meshStandardMaterial color="#8B4513" roughness={0.9} />
      </mesh>
      <mesh position={[0, 3.5, 0]} castShadow>
        <sphereGeometry args={[1.2, 8, 8]} />
        <meshStandardMaterial color="#2D5016" roughness={0.8} />
      </mesh>
    </group>
  );
};

const Trees = () => {
  const treePositions = useMemo(() => {
    const positions = [];
    const treeClusters = [
      { center: [-20, 0, -15], count: 5, radius: 10 },
      { center: [25, 0, -10], count: 4, radius: 8 },
      { center: [-15, 0, 15], count: 6, radius: 12 },
      { center: [30, 0, 20], count: 5, radius: 10 },
    ];
    
    treeClusters.forEach(cluster => {
      for (let i = 0; i < cluster.count; i++) {
        const angle = (i / cluster.count) * Math.PI * 2;
        const distance = Math.random() * cluster.radius;
        const x = cluster.center[0] + Math.cos(angle) * distance;
        const z = cluster.center[2] + Math.sin(angle) * distance;
        const scale = 0.8 + Math.random() * 0.6;
        
        positions.push({ position: [x, 0, z], scale: scale });
      }
    });
    
    return positions;
  }, []);

  return (
    <group>
      {treePositions.map((tree, index) => (
        <Tree key={index} position={tree.position} scale={tree.scale} />
      ))}
    </group>
  );
};

// Paved Path
const PavedPath = () => {
  const pathGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    
    const points = [];
    for (let i = 0; i < 50; i++) {
      const x = i * 2 - 50;
      const z = Math.sin(i * 0.3) * 5;
      points.push(new THREE.Vector2(x, z));
    }
    
    const pathWidth = 2;
    const leftPoints = points.map(p => new THREE.Vector2(p.x - pathWidth / 2, p.y));
    const rightPoints = points.map(p => new THREE.Vector2(p.x + pathWidth / 2, p.y)).reverse();
    
    shape.moveTo(leftPoints[0].x, leftPoints[0].y);
    leftPoints.forEach(p => shape.lineTo(p.x, p.y));
    rightPoints.forEach(p => shape.lineTo(p.x, p.y));
    shape.lineTo(leftPoints[0].x, leftPoints[0].y);
    
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: 0.1,
      bevelEnabled: false
    });
    
    geometry.rotateX(-Math.PI / 2);
    return geometry;
  }, []);

  return (
    <mesh geometry={pathGeometry} position={[0, 0.01, 0]} receiveShadow>
      <meshStandardMaterial color="#8B7355" roughness={0.9} metalness={0.1} />
    </mesh>
  );
};

// Enhanced Rock with carved text
const EnhancedRock = ({ post, position, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const rockRef = useRef();

  useFrame((state) => {
    if (rockRef.current && hovered) {
      rockRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.1;
    } else if (rockRef.current) {
      rockRef.current.position.y = position[1];
    }
  });

  const rockGeometry = useMemo(() => {
    const geometry = new THREE.DodecahedronGeometry(0.6, 0);
    const positions = geometry.attributes.position.array;
    
    for (let i = 0; i < positions.length; i += 3) {
      const seed = post.pid * 1000 + i;
      positions[i] += (Math.sin(seed) * 0.2);
      positions[i + 1] += (Math.cos(seed) * 0.2);
      positions[i + 2] += (Math.sin(seed * 2) * 0.2);
    }
    
    geometry.computeVertexNormals();
    return geometry;
  }, [post.pid]);

  const rockColor = useMemo(() => {
    const colors = ['#8B7D6B', '#A0826D', '#8E7E6E', '#736357', '#9B8B7A'];
    return colors[post.pid % colors.length];
  }, [post.pid]);

  return (
    <group ref={rockRef} position={position}>
      <mesh
        geometry={rockGeometry}
        onClick={(e) => {
          e.stopPropagation();
          onClick(post);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setHovered(false);
          document.body.style.cursor = 'default';
        }}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial
          color={hovered ? '#B8A896' : rockColor}
          roughness={0.9}
          metalness={0.1}
        />
      </mesh>

      {/* Text will be added later when dependencies are stable */}

      {hovered && (
        <pointLight
          position={[0, 1, 0]}
          intensity={1}
          distance={3}
          color="#FFD700"
        />
      )}
    </group>
  );
};

// Enhanced Rocks Group 
const EnhancedRocks = ({ posts, onPostClick }) => {
  return (
    <group>
      {posts.map((post, index) => {
        const x = index * 4 - 50;
        const z = Math.sin(index * 0.3) * 5;
        return (
          <EnhancedRock
            key={post.pid}
            post={post}
            position={[x, 0.5, z]}
            onClick={onPostClick}
          />
        );
      })}
    </group>
  );
};

// Flying Birds
const Bird = ({ position, speed, path }) => {
  const birdRef = useRef();
  const pathRef = useRef(0);

  useFrame((state) => {
    if (birdRef.current) {
      pathRef.current += speed;
      const t = (pathRef.current % path.length) / path.length;
      
      const point = path[Math.floor(t * path.length)];
      const nextPoint = path[Math.floor(((t + 0.01) * path.length)) % path.length];
      
      birdRef.current.position.set(point.x, point.y, point.z);
      
      const direction = new THREE.Vector3().subVectors(nextPoint, point).normalize();
      birdRef.current.lookAt(
        birdRef.current.position.x + direction.x,
        birdRef.current.position.y + direction.y,
        birdRef.current.position.z + direction.z
      );
      
      const wingFlap = Math.sin(state.clock.elapsedTime * 10) * 0.3;
      if (birdRef.current.children[1]) {
        birdRef.current.children[1].rotation.z = wingFlap;
      }
      if (birdRef.current.children[2]) {
        birdRef.current.children[2].rotation.z = -wingFlap;
      }
    }
  });

  return (
    <group ref={birdRef} position={position}>
      <mesh castShadow>
        <sphereGeometry args={[0.15, 6, 6]} />
        <meshStandardMaterial color="#4A4A4A" />
      </mesh>
      <mesh position={[-0.15, 0, 0]} rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[0.3, 0.05, 0.15]} />
        <meshStandardMaterial color="#333333" />
      </mesh>
      <mesh position={[0.15, 0, 0]} rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[0.3, 0.05, 0.15]} />
        <meshStandardMaterial color="#333333" />
      </mesh>
    </group>
  );
};

const Birds = () => {
  const birdData = useMemo(() => {
    const birds = [];
    const numBirds = 3;
    
    for (let i = 0; i < numBirds; i++) {
      const radius = 20 + Math.random() * 20;
      const height = 10 + Math.random() * 10;
      const path = [];
      
      for (let j = 0; j < 100; j++) {
        const angle = (j / 100) * Math.PI * 2;
        path.push(
          new THREE.Vector3(
            Math.cos(angle) * radius,
            height + Math.sin(j * 0.1) * 2,
            Math.sin(angle) * radius
          )
        );
      }
      
      birds.push({
        position: path[0],
        speed: 0.3 + Math.random() * 0.3,
        path: path
      });
    }
    
    return birds;
  }, []);

  return (
    <group>
      {birdData.map((bird, index) => (
        <Bird 
          key={index}
          position={bird.position}
          speed={bird.speed}
          path={bird.path}
        />
      ))}
    </group>
  );
};

// Metadata Overlay Component (moved from separate file for simplicity)
const MetadataOverlay = ({ post, onClose, onOpenPost }) => {
  const tags = post.tags.split(',').map(t => t.trim());

  return (
    <div className="metadata-overlay-backdrop" onClick={onClose}>
      <div className="metadata-overlay" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="metadata-content">
          <h2 className="post-title">{post.title}</h2>
          
          <div className="metadata-section">
            <div className="metadata-label">Published</div>
            <div className="metadata-value">{post.pubDate}</div>
          </div>

          <div className="metadata-section">
            <div className="metadata-label">Categories</div>
            <div className="metadata-tags">
              {tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          </div>

          <button 
            className="open-post-button"
            onClick={() => onOpenPost(post.pid)}
          >
            📜 Open Post
          </button>
        </div>
      </div>
    </div>
  );
};

const Blog3DWorldSimple = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeOfDay, setTimeOfDay] = useState('day');

  useEffect(() => {
    axios.get('https://rohitkulky.com/base/api/ps')
      .then(response => {
        const postsData = Array.isArray(response.data) ? response.data : [];
        setPosts(postsData.sort((a, b) => b.pid - a.pid));
        setLoading(false);
      })
      .catch(error => {
        console.error(`Error fetching posts: ${error}`);
        setLoading(false);
      });
  }, []);

  // Determine time of day based on local time
  useEffect(() => {
    const updateTimeOfDay = () => {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 12) {
        setTimeOfDay('morning');
      } else if (hour >= 12 && hour < 17) {
        setTimeOfDay('day');
      } else if (hour >= 17 && hour < 20) {
        setTimeOfDay('evening');
      } else {
        setTimeOfDay('night');
      }
    };
    
    updateTimeOfDay();
    const interval = setInterval(updateTimeOfDay, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const handleOpenPost = (postId) => {
    window.location.href = `/blog/post/${postId}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-text">Loading your journey...</div>
      </div>
    );
  }

  return (
    <div className="blog-3d-world">
      <Canvas
        camera={{ position: [0, 5, 15], fov: 60 }}
        shadows
      >
        {/* Dynamic lighting based on time of day */}
        <ambientLight intensity={timeOfDay === 'night' ? 0.2 : 0.5} />
        <directionalLight
          position={[10, 10, 5]}
          intensity={timeOfDay === 'night' ? 0.3 : 1}
          castShadow
          shadow-mapSize={[2048, 2048]}
        />
        
        {/* Sky background color based on time */}
        <color 
          attach="background" 
          args={[
            timeOfDay === 'night' ? '#0a1128' :
            timeOfDay === 'morning' ? '#87CEEB' :
            timeOfDay === 'day' ? '#87CEEB' :
            '#FF6B35'
          ]} 
        />
        
        <fog 
          attach="fog" 
          args={[
            timeOfDay === 'night' ? '#0a1128' : '#87CEEB',
            10,
            100
          ]} 
        />

        {/* Enhanced environment */}
        <EnhancedTerrain timeOfDay={timeOfDay} />
        <River />
        <Trees />
        <PavedPath />
        {timeOfDay !== 'night' && <Birds />}
        
        {/* Enhanced rocks with carved titles */}
        <EnhancedRocks posts={posts} onPostClick={setSelectedPost} />
        
        {/* Camera controls */}
        <OrbitControls />
      </Canvas>

      {selectedPost && (
        <MetadataOverlay
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onOpenPost={handleOpenPost}
        />
      )}

      <div className="ui-instructions">
        <p>🖱️ Drag to look around • Scroll to zoom</p>
        <p>🪨 Click on rocks to see post details</p>
        <p>📜 Click "Open Post" to read the full content</p>
        <p>🌅 Scene changes with time of day!</p>
      </div>
    </div>
  );
};

export default Blog3DWorldSimple;
